import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { FileSignature, ArrowRight, X } from "lucide-react";

/**
 * UnsignedContractBanner (iter76c) — premium, restrained onboarding nudge that
 * surfaces when the current user has a draft MSA awaiting signature.
 *
 * Design language: navy bar with sharp edges, monochrome treatment, one tonal
 * accent for the CTA. Dismissable per-session so it doesn't nag, but reappears
 * on next login until the contract is signed.
 *
 * Mount points: DashboardLayout (buyer + affiliate + admin), PublisherLayout.
 */
export default function UnsignedContractBanner() {
  const [draft, setDraft] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    // Per-session dismiss: clears on browser tab close
    if (sessionStorage.getItem("connexis_msa_banner_dismissed") === "1") {
      setDismissed(true);
      return;
    }
    api.get("/contracts/my")
      .then((r) => {
        const items = (r.data?.items || []);
        const unsigned = items.find((c) => c.status !== "signed");
        setDraft(unsigned || null);
      })
      .catch(() => {});
  }, []);

  const dismiss = () => {
    sessionStorage.setItem("connexis_msa_banner_dismissed", "1");
    setDismissed(true);
  };

  if (!draft || dismissed) return null;

  // Map role-specific contract type to the right portal contracts page
  const role = draft.role || "client";
  const portalMap = {
    client:    "/dashboard/contracts",
    publisher: "/publisher/contracts",
    affiliate: "/affiliate/contracts",
  };
  const portalUrl = portalMap[role] || "/dashboard/contracts";

  // Display title — prefer the backend-rendered template_title (e.g. "Lead
  // Purchase Agreement") which `_to_out()` already provides. Fall back to a
  // type-based mapping only if the API didn't include one (defensive — older
  // responses or shape drift).
  const typeLabel =
    draft.template_title ||
    {
      buyer_msa:     "Lead Purchase Agreement",
      publisher_msa: "Publisher Services Agreement",
      affiliate_msa: "Affiliate Programme Agreement",
    }[draft.contract_type || draft.type || draft.template] ||
    "Master Services Agreement";

  return (
    <div
      className="relative border border-slate-200 bg-white rounded-lg overflow-hidden shadow-soft"
      data-testid="unsigned-msa-banner"
    >
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#0F172A]" />
      <div className="flex items-center gap-4 px-5 py-4 pl-6">
        <span className="inline-grid place-items-center size-9 rounded-md bg-[#0F172A] text-white shrink-0">
          <FileSignature className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Action required · contract</span>
          </div>
          <h3 className="text-[14px] font-semibold text-slate-900 mt-0.5 leading-tight">
            Sign your {typeLabel} to unlock full platform access
          </h3>
          <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">
            Connexis Group Ltd has counter-signed and is awaiting your signature. Takes ~30 seconds — typed or drawn.
          </p>
        </div>
        <button
          onClick={() => nav(`${portalUrl}?sign=${encodeURIComponent(draft.id)}`)}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-[#0F172A] hover:bg-[#1e293b] text-white text-[12.5px] font-medium transition-colors shrink-0"
          data-testid="msa-banner-sign-cta"
        >
          Sign now <ArrowRight className="size-3.5" />
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-slate-400 hover:text-slate-700 shrink-0 transition-colors"
          data-testid="msa-banner-dismiss"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
